import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "@/data/content";
import { api } from "@/lib/api";
import { scrollToId } from "@/lib/scroll";

export default function ExitIntent() {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("seros_exit")) return;
    const onLeave = (e) => {
      if (e.clientY <= 0) { setShow(true); sessionStorage.setItem("seros_exit", "1"); }
    };
    const timer = setTimeout(() => document.addEventListener("mouseout", onLeave), 6000);
    return () => { clearTimeout(timer); document.removeEventListener("mouseout", onLeave); };
  }, []);

  const claim = async (e) => {
    e.preventDefault();
    try { await api.newsletter({ email, source: "exit-intent" }); } catch { /* ignore */ }
    setDone(true);
    toast.success("Offer unlocked! Show code FIRE10 on your next order.");
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div data-testid="exit-intent-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-charcoal/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <motion.div initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-cream rounded-2xl overflow-hidden max-w-2xl w-full grid md:grid-cols-2 shadow-2xl">
            <button onClick={() => setShow(false)} data-testid="exit-close" className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-cream/80 flex items-center justify-center hover:bg-cream"><X size={18} /></button>
            <div className="hidden md:block">
              <img src={IMAGES.hero} alt="Sero's fire-grilled" className="h-full w-full object-cover" />
            </div>
            <div className="p-8 md:p-10 flex flex-col justify-center">
              {!done ? (
                <>
                  <p className="text-terracotta text-xs uppercase tracking-[0.25em] mb-4">Before you go</p>
                  <h3 className="font-serif-display text-4xl text-ink leading-tight mb-3">Get <em className="text-terracotta not-italic">10% off</em> your first online order.</h3>
                  <p className="text-ink/65 text-sm mb-6">Drop your email and we'll send a code plus first dibs on catering deals.</p>
                  <form onSubmit={claim} data-testid="exit-form" className="flex flex-col gap-3">
                    <input required type="email" data-testid="exit-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
                      className="w-full border-b-2 border-ink/15 focus:border-terracotta outline-none py-2.5 bg-transparent transition-colors" />
                    <button type="submit" data-testid="exit-submit" className="bg-terracotta text-cream py-3.5 rounded-full text-sm hover:bg-ink transition-colors active:scale-95">Unlock My 10% Off</button>
                  </form>
                </>
              ) : (
                <div className="text-center">
                  <p className="text-terracotta text-xs uppercase tracking-[0.25em] mb-3">You're in 🔥</p>
                  <h3 className="font-serif-display text-4xl text-ink mb-3">Use code <span className="text-terracotta">FIRE10</span></h3>
                  <p className="text-ink/65 text-sm mb-6">Applies to your first online order. See you at the fire.</p>
                  <button onClick={() => { setShow(false); scrollToId("order"); }} data-testid="exit-order-now" className="bg-charcoal text-cream px-8 py-3.5 rounded-full text-sm hover:bg-terracotta transition-colors">Order Now</button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
